import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { NoFacilitySelectedComponent } from '../shared/components/no-facility-selected/no-facility-selected.component';
import { ToastComponent } from '../shared/components/toast/toast.component';
import { SignalRService } from '../core/signalr/signalr.service';
import { ChatManagerService } from '../managers/chat-manager.service';
import { SuiteGroupManagerService } from '../managers/suite-group-manager.service';
import { AppStore } from '../stores/app.store';
import { UiStore } from '../stores/ui.store';
import { ChatDockComponent } from './chat/chat-dock/chat-dock.component';
import { HeaderComponent } from './header.component';
import { isFacilityGateExcludedRoute } from './route-matchers';
import { SidebarComponent } from './sidebar.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    SidebarComponent,
    HeaderComponent,
    ToastComponent,
    ChatDockComponent,
    NoFacilitySelectedComponent,
  ],
  templateUrl: './layout.component.html',
})
export class LayoutComponent implements OnInit, OnDestroy {
  protected readonly uiStore = inject(UiStore);
  private readonly appStore = inject(AppStore);
  private readonly router = inject(Router);
  private readonly signalR = inject(SignalRService);
  private readonly suiteGroupManager = inject(SuiteGroupManagerService);
  private readonly chatManager = inject(ChatManagerService);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected readonly isChatEnabled = computed(() => this.chatManager.status()?.isEnabled === true);

  protected readonly showFacilityGate = computed(() => {
    const hasFacility = !!this.appStore.selectedFacilityId();

    if (hasFacility) {
      return false;
    }

    return !isFacilityGateExcludedRoute(this.currentUrl());
  });

  ngOnInit(): void {
    // Suite state affects every screen that references suites, so the subscription
    // lives here (the shell wrapping the whole authenticated session) rather than
    // on any single suite/suite-group screen.
    this.suiteGroupManager.subscribeToSuiteEvents();
    this.chatManager.subscribeToChatEvents();
    void this.signalR.start();
  }

  ngOnDestroy(): void {
    this.suiteGroupManager.unsubscribeFromSuiteEvents();
    this.chatManager.unsubscribeFromChatEvents();
  }
}
